import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, ShoppingBag, Plus, Edit, Trash2, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { getProducts } from '@/data/products';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_cfc143ec-270d-4208-9592-8eae45ed356e/artifacts/4ltgv78b_Untitled%20design-2.png';

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: 'Laser Printer',
    brand: '',
    description: '',
    price: '',
    stock: '',
    image_url: ''
  });

  useEffect(() => {
    // Load products from local data
    const localProducts = getProducts();
    setProducts(localProducts);
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCategoryChange = (value) => {
    setFormData({ ...formData, category: value });
  };

  const openAddDialog = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      category: 'Laser Printer',
      brand: '',
      description: '',
      price: '',
      stock: '',
      image_url: ''
    });
    setShowDialog(true);
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      category: product.category,
      brand: product.brand,
      description: product.description,
      price: product.price.toString(),
      stock: product.stock.toString(),
      image_url: product.image_url
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    toast.info('Demo mode: Product changes are not saved');
    setShowDialog(false);
  };

  const handleDelete = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      toast.info('Demo mode: Product not actually deleted');
    }
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }} data-testid="admin-products-page">
      {/* Sidebar */}
      <div className="admin-sidebar">
        <div style={{ padding: '0 2rem', marginBottom: '2rem' }}>
          <img src={LOGO_URL} alt="Om Jishnu" style={{ height: '50px', marginBottom: '0.5rem' }} />
          <h2 style={{ fontSize: '1.2rem', fontWeight: '700', color: '#A44A66' }}>ADMIN PANEL</h2>
        </div>
        <Link to="/admin" className="admin-sidebar-link" data-testid="admin-nav-dashboard">
          <BarChart3 size={20} style={{ marginRight: '0.75rem', display: 'inline' }} />
          Dashboard
        </Link>
        <Link to="/admin/products" className="admin-sidebar-link active" data-testid="admin-nav-products">
          <Package size={20} style={{ marginRight: '0.75rem', display: 'inline' }} />
          Products
        </Link>
        <Link to="/admin/orders" className="admin-sidebar-link" data-testid="admin-nav-orders">
          <ShoppingBag size={20} style={{ marginRight: '0.75rem', display: 'inline' }} />
          Orders
        </Link>
        <div style={{ padding: '2rem', borderTop: '1px solid #e5e5e5', marginTop: '2rem' }}>
          <Link to="/" style={{ color: '#666', textDecoration: 'none', fontSize: '0.9rem' }} data-testid="back-to-site">← Back to Site</Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="admin-main">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '2.5rem', fontWeight: '700' }} data-testid="products-title">Products</h1>
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button className="btn-primary" onClick={openAddDialog} data-testid="add-product-btn">
                <Plus size={20} style={{ marginRight: '0.5rem' }} />
                Add Product
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
              <DialogHeader>
                <DialogTitle data-testid="dialog-title">{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <Label htmlFor="name">Product Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    data-testid="product-name-input"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="category">Category *</Label>
                  <Select value={formData.category} onValueChange={handleCategoryChange}>
                    <SelectTrigger data-testid="product-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Laser Printer">Laser Printer</SelectItem>
                      <SelectItem value="Inkjet Printer">Inkjet Printer</SelectItem>
                      <SelectItem value="Ink Cartridge">Ink Cartridge</SelectItem>
                      <SelectItem value="Ink Bottle">Ink Bottle</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="form-group">
                  <Label htmlFor="brand">Brand *</Label>
                  <Input
                    id="brand"
                    name="brand"
                    value={formData.brand}
                    onChange={handleInputChange}
                    required
                    data-testid="product-brand-input"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="description">Description *</Label>
                  <textarea
                    id="description"
                    name="description"
                    className="form-textarea"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={4}
                    required
                    data-testid="product-description-input"
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <Label htmlFor="price">Price (₹) *</Label>
                    <Input
                      id="price"
                      name="price"
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={handleInputChange}
                      required
                      data-testid="product-price-input"
                    />
                  </div>
                  <div className="form-group">
                    <Label htmlFor="stock">Stock *</Label>
                    <Input
                      id="stock"
                      name="stock"
                      type="number"
                      value={formData.stock}
                      onChange={handleInputChange}
                      required
                      data-testid="product-stock-input"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <Label htmlFor="image_url">Image URL *</Label>
                  <Input
                    id="image_url"
                    name="image_url"
                    value={formData.image_url}
                    onChange={handleInputChange}
                    required
                    data-testid="product-image-input"
                  />
                </div>
                <Button type="submit" className="btn-primary" style={{ width: '100%' }} data-testid="save-product-btn">
                  {editingProduct ? 'Update Product' : 'Add Product'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Products Table */}
        <div style={{ background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }} data-testid="products-table">
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: '#f8f8f8', borderBottom: '2px solid #e5e5e5' }}>
                <tr>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Image</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Name</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Category</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Brand</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Price</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Stock</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id} style={{ borderBottom: '1px solid #e5e5e5' }} data-testid={`product-row-${product.id}`}>
                    <td style={{ padding: '1rem' }}>
                      <img src={product.image_url} alt={product.name} style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '8px' }} />
                    </td>
                    <td style={{ padding: '1rem', fontWeight: '600' }} data-testid="table-product-name">{product.name}</td>
                    <td style={{ padding: '1rem' }} data-testid="table-product-category">{product.category}</td>
                    <td style={{ padding: '1rem' }} data-testid="table-product-brand">{product.brand}</td>
                    <td style={{ padding: '1rem', fontWeight: '700', color: '#A44A66' }} data-testid="table-product-price">₹{product.price.toLocaleString()}</td>
                    <td style={{ padding: '1rem' }}>
                      <span
                        style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          background: product.stock > 10 ? '#d1fae5' : product.stock > 0 ? '#fef3c7' : '#fee2e2',
                          color: product.stock > 10 ? '#065f46' : product.stock > 0 ? '#92400e' : '#991b1b'
                        }}
                        data-testid="table-product-stock"
                      >
                        {product.stock}
                      </span>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => openEditDialog(product)}
                          style={{ padding: '0.5rem', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                          data-testid="edit-product-btn"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(product.id)}
                          style={{ padding: '0.5rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                          data-testid="delete-product-btn"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminProducts;